/**
 * Таблица маршрутов. Только форма — ни одного решения о доступе здесь нет:
 * маршрут лишь *объявляет* требования (`meta.public`, `meta.roles`), а исполняет
 * их `guards.ts`.
 *
 * `meta` типизирована в `types/router.d.ts`, поэтому опечатка в ключе
 * (`role` вместо `roles`) ломает компиляцию, а не превращается в открытый
 * маршрут в рантайме.
 */
import type { RouteRecordRaw } from 'vue-router'
import type { Role } from '@/types/api'
import AppShell from '@/components/layout/AppShell.vue'

/**
 * Единственное место, где перечислены «только для админа» маршруты.
 * `satisfies` ловит опечатку в имени роли на компиляции; `as const` даёт
 * `readonly`, который и ждёт `RouteMeta['roles']`.
 */
const ADMIN_ONLY = ['ROLE_ADMIN'] as const satisfies readonly Role[]

/**
 * Layout импортируется **не лениво**, в отличие от вью.
 *
 * Он нужен на каждом приватном маршруте, и ленивая загрузка дала бы лишний
 * round-trip ровно в тот момент, когда пользователь и так ждёт `bootstrap()`.
 * Вью — наоборот, все до одной ленивые: экран пользователей и экран сессий
 * никогда не нужны одновременно.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, title: 'Вход' },
  },

  {
    path: '/',
    component: AppShell,
    children: [
      // Корень — это не экран, а решение «куда по умолчанию».
      { path: '', redirect: { name: 'users' } },

      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/users/UsersListView.vue'),
        meta: { title: 'Пользователи' },
      },

      /**
       * ⚠️ ПОРЯДОК ЗНАЧИМ: `users/new` объявляется **до** `users/:id`.
       *
       * vue-router сортирует маршруты одной глубины так, что статический
       * сегмент выигрывает у параметра, но полагаться на это при равном
       * счёте нельзя — при перестановке `/users/new` отрендерит форму
       * редактирования и уйдёт в `GET /api/v1/users/new`, то есть в 404
       * от сервера на ровном месте. Не менять местами.
       */
      {
        path: 'users/new',
        name: 'user-create',
        component: () => import('@/views/users/UserCreateView.vue'),
        meta: { roles: ADMIN_ONLY, title: 'Новый пользователь' },
      },
      {
        path: 'users/:id',
        name: 'user-edit',
        component: () => import('@/views/users/UserEditView.vue'),
        // `props: true` — вью получает `id` пропом и не лезет в `useRoute()`;
        // так её можно смонтировать в тесте без роутера.
        props: true,
        meta: { roles: ADMIN_ONLY, title: 'Пользователь' },
      },

      {
        path: 'participants',
        name: 'participants',
        component: () => import('@/views/participants/ParticipantsListView.vue'),
        meta: { title: 'Участники' },
      },

      /**
       * ⚠️ ПОРЯДОК ЗНАЧИМ, и здесь литеральных сегментов **два**:
       * `participants/new` и `participants/import` объявляются до
       * `participants/:id`. Иначе `/participants/import` отрендерит карточку
       * участника и уйдёт в `GET /api/v1/participants/import` — то есть в
       * `VALIDATION_FAILED` (id не UUID) на ровном месте. Не менять местами.
       */
      {
        path: 'participants/new',
        name: 'participant-create',
        component: () => import('@/views/participants/ParticipantCreateView.vue'),
        meta: { roles: ADMIN_ONLY, title: 'Новый участник' },
      },
      {
        path: 'participants/import',
        name: 'participants-import',
        component: () => import('@/views/participants/ParticipantImportView.vue'),
        meta: { roles: ADMIN_ONLY, title: 'Импорт участников' },
      },
      {
        path: 'participants/:id',
        name: 'participant-edit',
        component: () => import('@/views/participants/ParticipantEditView.vue'),
        // `props: true` — вью получает `id` пропом и не лезет в `useRoute()`;
        // так её можно смонтировать в тесте без роутера.
        props: true,
        meta: { roles: ADMIN_ONLY, title: 'Участник' },
      },

      {
        path: 'account',
        name: 'profile',
        component: () => import('@/views/account/ProfileView.vue'),
        meta: { title: 'Мой профиль' },
      },
      {
        path: 'account/password',
        name: 'change-password',
        component: () => import('@/views/account/ChangePasswordView.vue'),
        meta: { title: 'Смена пароля' },
      },
      {
        path: 'account/sessions',
        name: 'sessions',
        component: () => import('@/views/account/SessionsView.vue'),
        meta: { title: 'Активные сессии' },
      },

      /**
       * 403 живёт **внутри** shell'а и **не** публичен: сюда попадает только
       * вошедший пользователь, которому не хватило роли. Анонима на `/forbidden`
       * гвард отправит на логин — «доступ запрещён» без объяснения, кому именно,
       * это худший из возможных ответов.
       */
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/errors/ForbiddenView.vue'),
        meta: { title: 'Доступ запрещён' },
      },
    ],
  },

  /**
   * 404 — публичный и **вне** shell'а: на неизвестный URL нельзя отвечать
   * редиректом на логин (это подсказывало бы, что за адресом что-то есть),
   * и нельзя требовать сайдбар с данными, которых может не быть.
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { public: true, title: 'Страница не найдена' },
  },
]

export default routes
